import { useState, useRef, useEffect, useMemo } from "react";
import { useGSAP } from "@gsap/react";
import styles from "./ImageSlider.module.css";

/**
 * ImageSlider affiche une séquence d'images comme une galerie défilante.
 * 
 * Le composant adapte les images pour s'intégrer parfaitement dans les modales
 * en préservant leurs proportions et en évitant qu'elles ne dépassent leur conteneur.
 *
 * @param {Object} params - Les paramètres pour le composant ImageSlider.
 * @param {Object} params.images - Un objet contenant les sources d'images et leurs textes alternatifs associés.
 * @param {Array<string>} params.images.src - Un tableau d'URLs d'images à afficher dans le slider.
 * @param {Array<string>} params.images.alt - Un tableau de textes alternatifs correspondant aux images.
 * @param {Array<string>} [params.images.captions] - Un tableau optionnel de légendes pour les images.
 * @return {JSX.Element} Le composant ImageSlider rendu.
 */
export default function ImageSlider({ images }) {
  const [idx, setIdx] = useState(0);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const imagesRef = useRef(images.src);

  const config = useMemo(() => {
    const autoPlay = Boolean(images?.autoPlay);
    const intervalMs =
      typeof images?.intervalMs === "number" && Number.isFinite(images.intervalMs)
        ? images.intervalMs
        : 4000;
    const pauseOnHover = images?.pauseOnHover !== false;
    return { autoPlay, intervalMs, pauseOnHover };
  }, [images]);

  // Réinitialiser l'état de chargement si les sources d'images changent
  useEffect(() => {
    if (imagesRef.current !== images.src) {
      setIsLoaded(false);
      imagesRef.current = images.src;
      setIdx(0);
    }
  }, [images.src]);

  // Auto-play (pour une lecture "animée" type slides)
  useEffect(() => {
    if (!config.autoPlay) return;
    if (isPaused) return;
    if (!Array.isArray(images?.src) || images.src.length <= 1) return;

    const timer = window.setInterval(() => {
      setIdx((prev) => (prev + 1) % images.src.length);
    }, config.intervalMs);

    return () => window.clearInterval(timer);
  }, [config.autoPlay, config.intervalMs, images?.src, isPaused]);

  useGSAP(
    () => {
      //Animation slider
    },
    { dependencies: [idx] },
  );

  const handleImageLoad = () => {
    setIsLoaded(true);
  };

  return (
    <div
      className={styles.slider}
      onPointerEnter={() => {
        if (config.pauseOnHover) setIsPaused(true);
      }}
      onPointerLeave={() => {
        if (config.pauseOnHover) setIsPaused(false);
      }}
    >
      <div className={styles.slide}>
        {!isLoaded && idx === 0 && <div className={styles.imagePlaceholder}></div>}
        {images.src.map((src, i) => (
          <img
            src={src}
            alt={images.alt[i] || `Image ${i + 1}`}
            key={`slide-img-${i}`}
            className={`${styles.imageSlide} ${i === idx && styles.showed}`}
            onLoad={i === 0 ? handleImageLoad : undefined}
          />
        ))}
      </div>

      {/* Légende pour l'image courante */}
      {images.captions && images.captions[idx] && (
        <div className={styles.imageCaption}>
          {images.captions[idx]}
        </div>
      )}

      <div className={styles.radioInputs}>
        {images.src.map((_, i) => (
          <div className={styles.radio} key={`radio-${i}`}>
            <input
              type="radio"
              id={`img_${i}`}
              name="imagesSlider"
              value={i}
              checked={i === idx}
              onChange={() => setIdx(i)}
            />
            <label htmlFor={`img_${i}`}>Image {i + 1}</label>
          </div>
        ))}
      </div>
    </div>
  );
}
